import React, { useState } from "react";
import { Panel, Table, Loader, Thumbnail, Tooltip, Icon, Dropdown, Pagination } from "@culturehq/components";
import * as CHQ from "chq";
import { Link } from "react-router-dom";

import { useFieldsLabels, useGet, usePagination } from "app/hooks";
import { DepartmentLink } from "app/links";
import { formatThousand, t, Trans } from "app/utils";

import getUserName from "app/utils/getUserName";
import Engagement from "./Engagement";

type UserResponseProps = {
  user: CHQ.User;
  days: string;
};

const UserResponse: React.FC<UserResponseProps> = ({ user, days }) => (
  <tr>
    <td className="w-30">
      <div className="flx-row">
        <Thumbnail image={user.avatar.thumbUrl} title={getUserName(user)} />
        <div className="flx-column">
          <Link aria-label={`${getUserName(user)}'s profile picture`} to={`/community/${user.id}`} tabIndex={0}>
            {getUserName(user)}
          </Link>
          <span>{user.title}</span>
        </div>
      </div>
    </td>
    <td className="w-20">
      {user.departments.map((department, key) => (
        <span key={department.id}>
          <DepartmentLink department={department} />
          {key !== user.departments.length - 1 && ", "}
        </span>
      ))}
    </td>
    <td className="w-10">{user.location?.name}</td>
    <td className="w-20 txt-center">{formatThousand(user.points)}</td>
    <td className="w-20 txt-center"><Engagement activity={user.activity} days={days} /></td>
  </tr>
);

const UserResponsesTable: React.FC = ({ children }) => {
  const labels = useFieldsLabels();

  return (
    <Table className="table-responsive">
      <thead>
        <tr>
          <th className="w-30">
            <Trans>Name</Trans>
          </th>
          <th className="w-20">
            <Trans>{labels.departments.singular}</Trans>
          </th>
          <th className="w-10">
            <Trans>Location</Trans>
          </th>
          <th className="w-20 txt-center">
            <Trans>Community Score</Trans>
          </th>
          <th className="w-20 txt-center tlp-included">
            <Trans>Engagement</Trans>
            <Tooltip tip={t("This represents a weighted value of activity.")} bottom>
              <Icon icon="info" />
            </Tooltip>
          </th>
        </tr>
      </thead>
      <tbody>
        {children}
      </tbody>
    </Table>
  );
};

export type ListUserResponses = {
  users: CHQ.User[];
  pagination: any;
};

type UserResponsesProps = {
  days: string;
};

const UserResponses: React.FC<UserResponsesProps> = ({ days }) => {
  const [page, onPageClick] = usePagination();
  const get = useGet<ListUserResponses>(`/analytics/engaged_users?time=${days}&page=${page}`);

  if (get.getting) {
    return <Loader loading />;
  }

  if (get.error) {
    throw get.error;
  }

  if (get.got.users.length === 0) {
    return (
      <UserResponsesTable>
        <tr>
          <td colSpan={3} align="center">
            <Trans>No data</Trans>
          </td>
        </tr>
      </UserResponsesTable>
    );
  }

  return (
    <>
      <UserResponsesTable>
        {get.got.users.map(user => (
          <UserResponse key={user.id} user={user} days={days} />
        ))}
      </UserResponsesTable>
      <Pagination {...get.got.pagination} onClick={onPageClick} />
    </>
  );
};

const MostEngagedUsers: React.FC = () => {
  const [days, setDays] = useState("30");
  const [timeFilterLabel, setTimeFilterLabel] = useState("Last 30 Days");

  const setTimeFilter = (value: string) => {
    setDays(value);
    if (value === "all") {
      setTimeFilterLabel("Including all time");
      return;
    }

    setTimeFilterLabel(`Last ${value} Days`);
  };

  return (
    <Panel>
      <Panel.Body className="card_holder">
        <div className="card_title most_engaged_users">
          <h3>
            <Trans>Most Engaged Users</Trans>
          </h3>
          <div>
            <Dropdown selected={days} onChange={setTimeFilter}>
              <Dropdown.Button>
                <Trans>{timeFilterLabel}</Trans>
              </Dropdown.Button>
              <Dropdown.ListBox className="custom_option">
                <Dropdown.Option value="30">
                  <Trans>Last 30 days</Trans>
                </Dropdown.Option>
                <Dropdown.Option value="60">
                  <Trans>Last 60 days</Trans>
                </Dropdown.Option>
                <Dropdown.Option value="90">
                  <Trans>Last 90 days</Trans>
                </Dropdown.Option>
                <Dropdown.Option value="all">
                  <Trans>All time</Trans>
                </Dropdown.Option>
              </Dropdown.ListBox>
            </Dropdown>
          </div>
        </div>
        <div className="card_info">
          <UserResponses days={days} />
        </div>
      </Panel.Body>
    </Panel>
  );
};

export default MostEngagedUsers;
